import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  Plus,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Edit,
  Trash2,
  Copy,
  Flame,
  CheckCircle2,
  Clock,
  GripVertical,
  MessageSquare,
  FileDown,
  Layers,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { plansAPI, weeksAPI, daysAPI, blocksAPI } from '../lib/api';
import { planWeeks, daysUntil, formatRange } from '../lib/dates';
import { DisciplineBadge } from '../lib/disciplines';
import { blockTypeMeta } from '../lib/blockTypes';
import { dayFatigue, fatigueLevel, matchForLevel } from '../lib/fatigue';
import { exportPlanToPDF } from '../utils/pdfExport';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Modal } from '../components/ui/Modal';
import { PlanForm } from '../components/forms/PlanForm';
import { WeekForm } from '../components/forms/WeekForm';
import { DayForm } from '../components/forms/DayForm';
import { BlockForm } from '../components/forms/BlockForm';
import { BlockItemForm } from '../components/forms/BlockItemForm';
import type { Week, Day, TrainingBlock, BlockItem, BlockType, Shift } from '../types';
import clsx from 'clsx';

const shiftLabels: Record<Shift, string> = {
  morning: 'Mañana',
  afternoon: 'Tarde',
  evening: 'Noche',
};

// ¿El alumno completó este ejercicio? (series hechas ≥ prescritas)
function itemIsDone(item: BlockItem): boolean {
  const done = item.setsCompleted ?? 0;
  return done > 0 && done >= Math.max(1, item.sets ?? 1);
}

// Adherencia de un día: ejercicios completados / total (recursivo).
function dayAdherence(blocks: TrainingBlock[]): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const b of blocks) {
    for (const it of b.items ?? []) {
      total++;
      if (itemIsDone(it)) done++;
    }
    if (b.children) {
      const c = dayAdherence(b.children);
      done += c.done;
      total += c.total;
    }
  }
  return { done, total };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// ============================================================
// SortableBlockItem — draggable exercise inside a block
// ============================================================
interface SortableBlockItemProps {
  item: BlockItem;
  blockId: string;
  onEdit: () => void;
  onDelete: () => void;
}

function SortableBlockItem({ item, blockId, onEdit, onDelete }: SortableBlockItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `item-${item.id}`,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 p-2 rounded bg-dark-800/50 group">
      <button
        {...attributes}
        {...listeners}
        className="touch-none cursor-grab active:cursor-grabbing p-0.5 -ml-0.5 text-dark-600 hover:text-dark-300 transition-colors"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar ejercicio"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-dark-100 truncate">{item.exercise?.name}</p>
        <div className="flex items-center gap-2 text-xs text-dark-400 flex-wrap">
          {(item.setsCompleted ?? 0) > 0 &&
            (itemIsDone(item) ? (
              <span className="flex items-center gap-0.5 text-primary-400 font-medium">
                <CheckCircle2 className="w-3 h-3" />
                Hecho
              </span>
            ) : (
              <span className="text-sky-400">
                {item.setsCompleted}/{Math.max(1, item.sets ?? 1)} hechas
              </span>
            ))}
          {item.sets ? <span>{item.sets} series</span> : null}
          {item.reps ? <span>× {item.reps}</span> : null}
          {item.weight ? <span className="text-primary-400">{item.weight}</span> : null}
          {item.restSeconds ? (
            <span className="flex items-center gap-0.5">
              <Clock className="w-3 h-3" />
              {item.restSeconds}s
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {item.comments && item.comments.length > 0 && (
          <span className="badge badge-accent text-xs">
            <MessageSquare className="w-3 h-3" />
            {item.comments.length}
          </span>
        )}
        <button
          onClick={onEdit}
          className="p-1 rounded text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors"
        >
          <Edit className="w-3 h-3" />
        </button>
        <button
          onClick={onDelete}
          className="p-1 rounded text-dark-400 hover:text-red-400 hover:bg-dark-700 transition-colors"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

// ============================================================
// SortableBlock — recursive draggable block (with children + items)
// ============================================================
interface SortableBlockProps {
  block: TrainingBlock;
  depth: number;
  onAddSubBlock: (parentBlockId: string, dayId: string) => void;
  onAddItem: (blockId: string) => void;
  onEditBlock: (block: TrainingBlock) => void;
  onDeleteBlock: (blockId: string) => void;
  onEditItem: (blockId: string, item: BlockItem) => void;
  onDeleteItem: (blockId: string, itemId: string) => void;
  onItemsReordered: (blockId: string, itemIds: string[]) => void;
  onChildrenReordered: (parentBlockId: string, dayId: string, blockIds: string[]) => void;
}

function SortableBlock(props: SortableBlockProps) {
  const {
    block,
    depth,
    onAddSubBlock,
    onAddItem,
    onEditBlock,
    onDeleteBlock,
    onEditItem,
    onDeleteItem,
    onItemsReordered,
    onChildrenReordered,
  } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `block-${block.id}`,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const blockMeta = blockTypeMeta[block.type as BlockType] ?? blockTypeMeta.exercise;
  const BlockIcon = blockMeta.icon;
  const items = block.items ?? [];
  const children = block.children ?? [];
  const hasChildren = children.length > 0;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleItemsDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = items.map((i) => `item-${i.id}`);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(items, oldIndex, newIndex).map((i) => i.id);
    onItemsReordered(block.id, reordered);
  };

  const handleChildrenDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = children.map((c) => `block-${c.id}`);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(children, oldIndex, newIndex).map((c) => c.id);
    onChildrenReordered(block.id, block.dayId, reordered);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx('rounded-lg bg-dark-900/50 p-3', `block-${block.type}`, depth > 0 && 'ml-4')}
    >
      {/* Block Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            {...attributes}
            {...listeners}
            className="touch-none cursor-grab active:cursor-grabbing text-dark-600 hover:text-dark-300 transition-colors"
            title="Arrastrar para reordenar"
            aria-label="Arrastrar bloque"
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <BlockIcon className="w-4 h-4 text-dark-400 flex-shrink-0" />
          <span className="font-medium text-dark-200 text-sm truncate">
            {block.name || blockMeta.label}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onAddSubBlock(block.id, block.dayId)}
            className="p-1 rounded text-dark-400 hover:text-primary-400 hover:bg-dark-700 transition-colors"
            title="Agregar sub-bloque"
          >
            <Layers className="w-3.5 h-3.5" />
          </button>
          {!hasChildren && (
            <button
              onClick={() => onAddItem(block.id)}
              className="p-1 rounded text-dark-400 hover:text-primary-400 hover:bg-dark-700 transition-colors"
              title="Agregar ejercicio"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => onEditBlock(block)}
            className="p-1 rounded text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors"
          >
            <Edit className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDeleteBlock(block.id)}
            className="p-1 rounded text-dark-400 hover:text-red-400 hover:bg-dark-700 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {block.notes && <p className="text-xs text-dark-400 mb-2">{block.notes}</p>}

      {/* Children (sub-blocks) — recursive */}
      {hasChildren && (
        <div className="space-y-2 mt-2">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleChildrenDragEnd}>
            <SortableContext
              items={children.map((c) => `block-${c.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {children.map((child) => (
                <SortableBlock key={child.id} {...props} block={child} depth={depth + 1} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Items (only on leaf blocks) */}
      {!hasChildren && items.length > 0 && (
        <div className="space-y-1">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleItemsDragEnd}>
            <SortableContext
              items={items.map((i) => `item-${i.id}`)}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item) => (
                <SortableBlockItem
                  key={item.id}
                  item={item}
                  blockId={block.id}
                  onEdit={() => onEditItem(block.id, item)}
                  onDelete={() => onDeleteItem(block.id, item.id)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {/* Empty leaf block: prompt to add exercise */}
      {!hasChildren && items.length === 0 && (
        <button
          onClick={() => onAddItem(block.id)}
          className="w-full py-2 text-sm text-dark-400 hover:text-primary-400 border border-dashed border-dark-600 rounded hover:border-primary-500/50 transition-colors"
        >
          + Agregar ejercicio
        </button>
      )}
    </div>
  );
}

// ============================================================
// PlanDetail — main page
// ============================================================
export function PlanDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Modal state
  const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);
  const [isDeletePlanOpen, setIsDeletePlanOpen] = useState(false);
  const [isWeekFormOpen, setIsWeekFormOpen] = useState(false);
  const [editingWeek, setEditingWeek] = useState<Week | null>(null);
  const [isDayFormOpen, setIsDayFormOpen] = useState(false);
  const [dayFormWeekId, setDayFormWeekId] = useState<string>('');
  const [editingDay, setEditingDay] = useState<Day | null>(null);
  const [isBlockFormOpen, setIsBlockFormOpen] = useState(false);
  const [blockFormDayId, setBlockFormDayId] = useState<string>('');
  const [blockFormParentId, setBlockFormParentId] = useState<string | null>(null);
  const [editingBlock, setEditingBlock] = useState<TrainingBlock | null>(null);
  const [isItemFormOpen, setIsItemFormOpen] = useState(false);
  const [itemFormBlockId, setItemFormBlockId] = useState<string>('');
  const [editingItem, setEditingItem] = useState<BlockItem | null>(null);

  const [expandedWeeks, setExpandedWeeks] = useState<Record<string, boolean>>({});
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({});

  const { data: plan, isLoading, error } = useQuery({
    queryKey: ['plan', id],
    queryFn: () => plansAPI.getById(id!),
    enabled: !!id,
  });

  // Top-level day-block sensors for the per-day DnD context
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // === Mutations ===
  const updatePlanMutation = useMutation({
    mutationFn: (data: any) => plansAPI.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setIsEditPlanOpen(false);
      toast.success('Plan actualizado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deletePlanMutation = useMutation({
    mutationFn: () => plansAPI.delete(id!),
    onSuccess: () => {
      navigate(`/students/${plan?.studentId}`);
      toast.success('Plan eliminado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const createWeekMutation = useMutation({
    mutationFn: (data: any) => weeksAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setIsWeekFormOpen(false);
      toast.success('Semana creada');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateWeekMutation = useMutation({
    mutationFn: ({ weekId, data }: { weekId: string; data: any }) => weeksAPI.update(weekId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setEditingWeek(null);
      toast.success('Semana actualizada');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteWeekMutation = useMutation({
    mutationFn: (weekId: string) => weeksAPI.delete(weekId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      toast.success('Semana eliminada');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const duplicateWeekMutation = useMutation({
    mutationFn: (weekId: string) => weeksAPI.duplicate(weekId, {}),
    onSuccess: (newWeek) => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      toast.success(`Semana ${newWeek.weekNumber} creada como copia`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const createDayMutation = useMutation({
    mutationFn: (data: any) => daysAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setIsDayFormOpen(false);
      toast.success('Día creado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateDayMutation = useMutation({
    mutationFn: ({ dayId, data }: { dayId: string; data: any }) => daysAPI.update(dayId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setEditingDay(null);
      toast.success('Día actualizado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteDayMutation = useMutation({
    mutationFn: (dayId: string) => daysAPI.delete(dayId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      toast.success('Día eliminado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const duplicateDayMutation = useMutation({
    mutationFn: (dayId: string) => daysAPI.duplicate(dayId, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      toast.success('Día duplicado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const createBlockMutation = useMutation({
    mutationFn: (data: any) => blocksAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setIsBlockFormOpen(false);
      setBlockFormParentId(null);
      toast.success('Bloque creado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateBlockMutation = useMutation({
    mutationFn: ({ blockId, data }: { blockId: string; data: any }) => blocksAPI.update(blockId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setEditingBlock(null);
      toast.success('Bloque actualizado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteBlockMutation = useMutation({
    mutationFn: (blockId: string) => blocksAPI.delete(blockId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      toast.success('Bloque eliminado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reorderBlocksMutation = useMutation({
    mutationFn: (params: { dayId: string; parentBlockId: string | null; blockIds: string[] }) =>
      blocksAPI.reorder(params),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plan', id] }),
    onError: (err: Error) => toast.error(`Error al reordenar bloques: ${err.message}`),
  });

  const addItemMutation = useMutation({
    mutationFn: ({ blockId, data }: { blockId: string; data: any }) => blocksAPI.addItem(blockId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setIsItemFormOpen(false);
      toast.success('Ejercicio agregado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ blockId, itemId, data }: { blockId: string; itemId: string; data: any }) =>
      blocksAPI.updateItem(blockId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      setEditingItem(null);
      toast.success('Ejercicio actualizado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteItemMutation = useMutation({
    mutationFn: ({ blockId, itemId }: { blockId: string; itemId: string }) =>
      blocksAPI.deleteItem(blockId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plan', id] });
      toast.success('Ejercicio eliminado');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const reorderItemsMutation = useMutation({
    mutationFn: ({ blockId, itemIds }: { blockId: string; itemIds: string[] }) =>
      blocksAPI.reorderItems(blockId, itemIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['plan', id] }),
    onError: (err: Error) => toast.error(`Error al reordenar ejercicios: ${err.message}`),
  });

  // === Handlers ===
  const toggleWeek = (weekId: string) => setExpandedWeeks((p) => ({ ...p, [weekId]: !p[weekId] }));
  const toggleDay = (dayId: string) => setExpandedDays((p) => ({ ...p, [dayId]: !p[dayId] }));

  // Expandir / colapsar todas las semanas y días de una.
  const expandAll = () => {
    const weeksList = plan?.weeks ?? [];
    setExpandedWeeks(Object.fromEntries(weeksList.map((w: Week) => [w.id, true] as const)));
    setExpandedDays(
      Object.fromEntries(
        weeksList.flatMap((w: Week) => (w.days ?? []).map((d: Day) => [d.id, true] as const))
      )
    );
  };
  const collapseAll = () => {
    setExpandedWeeks({});
    setExpandedDays({});
  };

  const openDayForm = (weekId: string) => {
    setDayFormWeekId(weekId);
    setIsDayFormOpen(true);
  };

  const openBlockForm = (dayId: string, parentBlockId: string | null = null) => {
    setBlockFormDayId(dayId);
    setBlockFormParentId(parentBlockId);
    setIsBlockFormOpen(true);
  };

  const openItemForm = (blockId: string) => {
    setItemFormBlockId(blockId);
    setIsItemFormOpen(true);
  };

  const handleTopLevelDragEnd = (dayId: string, blocks: TrainingBlock[], event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = blocks.map((b) => `block-${b.id}`);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(blocks, oldIndex, newIndex).map((b) => b.id);
    reorderBlocksMutation.mutate({ dayId, parentBlockId: null, blockIds: reordered });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="card text-center py-12">
        <h2 className="text-xl font-semibold text-dark-300 mb-2">Plan no encontrado</h2>
        <Link to="/students" className="btn-primary mt-4">
          <ArrowLeft className="w-4 h-4" />
          Volver a Alumnos
        </Link>
      </div>
    );
  }

  const dayNames = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
  const hasWeeks = !!plan.weeks && plan.weeks.length > 0;
  const anyExpanded = (plan.weeks ?? []).some((w: Week) => expandedWeeks[w.id]);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Back button */}
      <motion.div variants={itemVariants}>
        <Link
          to={`/students/${plan.studentId}`}
          className="inline-flex items-center gap-2 text-dark-400 hover:text-dark-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al Alumno
        </Link>
      </motion.div>

      {/* Plan Header */}
      <motion.div variants={itemVariants} className="card">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-display font-bold text-dark-50">{plan.name}</h1>
              <span className={clsx('badge', plan.isActive ? 'badge-primary' : 'badge-neutral')}>
                {plan.isActive ? 'Activo' : 'Finalizado'}
              </span>
              <DisciplineBadge discipline={plan.discipline} />
            </div>
            {plan.description && <p className="text-dark-400">{plan.description}</p>}
            <div className="flex items-center gap-4 mt-4 text-sm text-dark-400 flex-wrap">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatRange(plan.startDate, plan.endDate)}
              </div>
              {plan.endDate && (
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {planWeeks(plan.startDate, plan.endDate)} sem planificadas
                </div>
              )}
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {plan.weeks?.length || 0} sem cargadas
              </div>
              {plan.endDate && (() => {
                const d = daysUntil(plan.endDate);
                if (d < 0) return (
                  <span className="flex items-center gap-1 text-red-400 font-medium">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Vencido
                  </span>
                );
                if (d <= 7) return (
                  <span className="flex items-center gap-1 text-amber-400 font-medium">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    {d === 0 ? 'Vence hoy' : `Vence en ${d} día${d !== 1 ? 's' : ''}`}
                  </span>
                );
                return <span>{d} días restantes</span>;
              })()}
            </div>
            {plan.student && (
              <Link
                to={`/students/${plan.studentId}`}
                className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-lg bg-dark-800 text-dark-300 hover:text-dark-100 transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-primary-500/20 flex items-center justify-center text-xs text-primary-400">
                  {plan.student.firstName[0]}
                  {plan.student.lastName[0]}
                </div>
                {plan.student.firstName} {plan.student.lastName}
              </Link>
            )}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => exportPlanToPDF(plan)} className="btn-secondary btn-sm" title="Exportar a PDF">
              <FileDown className="w-4 h-4" />
              PDF
            </button>
            <button onClick={() => setIsEditPlanOpen(true)} className="btn-secondary btn-sm">
              <Edit className="w-4 h-4" />
              Editar
            </button>
            <button onClick={() => setIsDeletePlanOpen(true)} className="btn-danger btn-sm">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* Weeks Section */}
      <motion.div variants={itemVariants} className="space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-xl font-semibold text-dark-50">Semanas de Entrenamiento</h2>
          <div className="flex items-center gap-2">
            {hasWeeks && (
              <button
                onClick={anyExpanded ? collapseAll : expandAll}
                className="btn-secondary btn-sm"
                title={anyExpanded ? 'Colapsar todas las semanas y días' : 'Expandir todas las semanas y días'}
              >
                {anyExpanded ? (
                  <ChevronsDownUp className="w-4 h-4" />
                ) : (
                  <ChevronsUpDown className="w-4 h-4" />
                )}
                {anyExpanded ? 'Colapsar todo' : 'Expandir todo'}
              </button>
            )}
            <button onClick={() => setIsWeekFormOpen(true)} className="btn-primary btn-sm">
              <Plus className="w-4 h-4" />
              Nueva Semana
            </button>
          </div>
        </div>

        {plan.weeks && plan.weeks.length > 0 ? (
          <div className="space-y-4">
            {plan.weeks.map((week: Week) => (
              <div key={week.id} className="card">
                {/* Week Header */}
                <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleWeek(week.id)}>
                  <div className="flex items-center gap-3">
                    <button className="p-1">
                      {expandedWeeks[week.id] ? (
                        <ChevronDown className="w-5 h-5 text-dark-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-dark-400" />
                      )}
                    </button>
                    <div>
                      <h3 className="font-semibold text-dark-100">
                        Semana {week.weekNumber}
                        {week.name && <span className="text-dark-400 font-normal ml-2">- {week.name}</span>}
                      </h3>
                      <p className="text-sm text-dark-400">{week.days?.length || 0} días programados</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => duplicateWeekMutation.mutate(week.id)}
                      disabled={duplicateWeekMutation.isPending}
                      className="p-1.5 rounded-lg text-dark-400 hover:text-primary-400 hover:bg-dark-700 transition-colors disabled:opacity-50"
                      title="Duplicar semana"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditingWeek(week)}
                      className="p-1.5 rounded-lg text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteWeekMutation.mutate(week.id)}
                      className="p-1.5 rounded-lg text-dark-400 hover:text-red-400 hover:bg-dark-700 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Week Content */}
                <AnimatePresence>
                  {expandedWeeks[week.id] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="pt-4 mt-4 border-t border-dark-700/50 space-y-4">
                        {week.notes && (
                          <p className="text-sm text-dark-400 bg-dark-800/50 p-3 rounded-lg">{week.notes}</p>
                        )}

                        {/* Days */}
                        {week.days && week.days.length > 0 ? (
                          <div className="space-y-3">
                            {week.days.map((day: Day) => {
                              const dayBlocks = day.blocks ?? [];
                              const adh = dayAdherence(dayBlocks);
                              const fScore = !day.restDay && dayBlocks.length > 0 ? dayFatigue(day) : 0;
                              const fLevel = fScore > 0 ? fatigueLevel(fScore) : null;
                              const studentLevel = plan.student?.trainingLevel;
                              const match = fScore > 0 && studentLevel ? matchForLevel(fScore, studentLevel, plan.discipline) : null;
                              return (
                                <div
                                  key={day.id}
                                  className={clsx(
                                    'rounded-xl border',
                                    day.restDay
                                      ? 'bg-dark-800/30 border-dark-700/30'
                                      : 'bg-dark-800/50 border-dark-700/50'
                                  )}
                                >
                                  {/* Day Header */}
                                  <div
                                    className="flex items-center justify-between p-3 cursor-pointer"
                                    onClick={() => toggleDay(day.id)}
                                  >
                                    <div className="flex items-center gap-3">
                                      <button className="p-0.5">
                                        {expandedDays[day.id] ? (
                                          <ChevronDown className="w-4 h-4 text-dark-400" />
                                        ) : (
                                          <ChevronRight className="w-4 h-4 text-dark-400" />
                                        )}
                                      </button>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-dark-100">{dayNames[day.dayNumber]}</span>
                                          {day.shift && (
                                            <span className="badge badge-accent text-xs">
                                              {shiftLabels[day.shift]}
                                            </span>
                                          )}
                                          {day.name && <span className="text-sm text-dark-400">- {day.name}</span>}
                                          {day.restDay && (
                                            <span className="badge badge-neutral text-xs">Descanso</span>
                                          )}
                                          {fLevel && (
                                            <span
                                              className={clsx(
                                                'badge text-xs inline-flex items-center gap-1 border',
                                                fLevel.classes
                                              )}
                                              title={`Carga del día: ${fScore}`}
                                            >
                                              <Flame className="w-3 h-3" />
                                              {fLevel.label}
                                            </span>
                                          )}
                                          {match && (
                                            <span className={clsx('badge text-xs border', match.classes)}>
                                              {match.label}
                                            </span>
                                          )}
                                          {!day.restDay && adh.total > 0 && (
                                            <span
                                              className={clsx(
                                                'badge text-xs inline-flex items-center gap-1 border',
                                                adh.done === adh.total
                                                  ? 'text-primary-300 border-primary-500/30 bg-primary-500/10'
                                                  : 'text-dark-300 border-dark-600'
                                              )}
                                              title="Ejercicios completados por el alumno"
                                            >
                                              <CheckCircle2 className="w-3 h-3" />
                                              {adh.done}/{adh.total}
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-xs text-dark-500">
                                          {dayBlocks.length} bloques
                                          {fScore > 0 && <span className="text-dark-600"> · carga {fScore}</span>}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <button
                                        onClick={() => duplicateDayMutation.mutate(day.id)}
                                        disabled={duplicateDayMutation.isPending}
                                        className="p-1 rounded text-dark-400 hover:text-primary-400 hover:bg-dark-700 transition-colors disabled:opacity-50"
                                        title="Duplicar día"
                                      >
                                        <Copy className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => setEditingDay(day)}
                                        className="p-1 rounded text-dark-400 hover:text-dark-100 hover:bg-dark-700 transition-colors"
                                      >
                                        <Edit className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={() => deleteDayMutation.mutate(day.id)}
                                        className="p-1 rounded text-dark-400 hover:text-red-400 hover:bg-dark-700 transition-colors"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>

                                  {/* Day Content - Blocks */}
                                  <AnimatePresence>
                                    {expandedDays[day.id] && !day.restDay && (
                                      <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: 'auto', opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        className="overflow-hidden"
                                      >
                                        <div className="p-3 pt-0 space-y-3">
                                          {dayBlocks.length > 0 && (
                                            <DndContext
                                              sensors={sensors}
                                              collisionDetection={closestCenter}
                                              onDragEnd={(e) => handleTopLevelDragEnd(day.id, dayBlocks, e)}
                                            >
                                              <SortableContext
                                                items={dayBlocks.map((b) => `block-${b.id}`)}
                                                strategy={verticalListSortingStrategy}
                                              >
                                                <div className="space-y-3">
                                                  {dayBlocks.map((block) => (
                                                    <SortableBlock
                                                      key={block.id}
                                                      block={block}
                                                      depth={0}
                                                      onAddSubBlock={(parentId, dId) => openBlockForm(dId, parentId)}
                                                      onAddItem={openItemForm}
                                                      onEditBlock={setEditingBlock}
                                                      onDeleteBlock={(bid) => deleteBlockMutation.mutate(bid)}
                                                      onEditItem={(bid, item) => {
                                                        setItemFormBlockId(bid);
                                                        setEditingItem(item);
                                                      }}
                                                      onDeleteItem={(bid, iid) =>
                                                        deleteItemMutation.mutate({ blockId: bid, itemId: iid })
                                                      }
                                                      onItemsReordered={(bid, itemIds) =>
                                                        reorderItemsMutation.mutate({ blockId: bid, itemIds })
                                                      }
                                                      onChildrenReordered={(parentId, dId, blockIds) =>
                                                        reorderBlocksMutation.mutate({
                                                          dayId: dId,
                                                          parentBlockId: parentId,
                                                          blockIds,
                                                        })
                                                      }
                                                    />
                                                  ))}
                                                </div>
                                              </SortableContext>
                                            </DndContext>
                                          )}

                                          {/* Add Block Button */}
                                          <button
                                            onClick={() => openBlockForm(day.id, null)}
                                            className="w-full py-2 text-sm text-dark-400 hover:text-primary-400 border border-dashed border-dark-600 rounded-lg hover:border-primary-500/50 transition-colors flex items-center justify-center gap-1"
                                          >
                                            <Plus className="w-4 h-4" />
                                            Agregar bloque
                                          </button>
                                        </div>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </div>
                              );
                            })}
                          </div>
                        ) : null}

                        {/* Add Day Button */}
                        <button
                          onClick={() => openDayForm(week.id)}
                          className="w-full py-3 text-dark-400 hover:text-primary-400 border border-dashed border-dark-600 rounded-xl hover:border-primary-500/50 transition-colors flex items-center justify-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Agregar día
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <Calendar className="w-16 h-16 mx-auto text-dark-600 mb-4" />
            <h3 className="text-lg font-semibold text-dark-300 mb-2">Sin semanas programadas</h3>
            <p className="text-dark-400 mb-6">Comienza agregando la primera semana de entrenamiento</p>
            <button onClick={() => setIsWeekFormOpen(true)} className="btn-primary">
              <Plus className="w-5 h-5" />
              Nueva Semana
            </button>
          </div>
        )}
      </motion.div>

      {/* Modals */}
      <Modal isOpen={isEditPlanOpen} onClose={() => setIsEditPlanOpen(false)} title="Editar Plan" size="lg">
        <PlanForm
          studentId={plan.studentId}
          initialData={plan}
          onSubmit={(data) => updatePlanMutation.mutate(data)}
          onCancel={() => setIsEditPlanOpen(false)}
          isLoading={updatePlanMutation.isPending}
        />
      </Modal>

      <Modal isOpen={isDeletePlanOpen} onClose={() => setIsDeletePlanOpen(false)} title="Eliminar Plan">
        <div className="space-y-4">
          <p className="text-dark-300">
            ¿Estás seguro de que deseas eliminar el plan{' '}
            <span className="font-semibold text-dark-100">{plan.name}</span>? Esta acción no se puede deshacer.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setIsDeletePlanOpen(false)} className="btn-secondary">
              Cancelar
            </button>
            <button
              onClick={() => deletePlanMutation.mutate()}
              disabled={deletePlanMutation.isPending}
              className="btn-danger"
            >
              {deletePlanMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Trash2 className="w-4 h-4" />
                  Eliminar
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isWeekFormOpen || !!editingWeek}
        onClose={() => {
          setIsWeekFormOpen(false);
          setEditingWeek(null);
        }}
        title={editingWeek ? 'Editar Semana' : 'Nueva Semana'}
      >
        <WeekForm
          planId={id!}
          nextWeekNumber={(plan.weeks?.length || 0) + 1}
          initialData={editingWeek || undefined}
          onSubmit={(data) => {
            if (editingWeek) updateWeekMutation.mutate({ weekId: editingWeek.id, data });
            else createWeekMutation.mutate(data);
          }}
          onCancel={() => {
            setIsWeekFormOpen(false);
            setEditingWeek(null);
          }}
          isLoading={createWeekMutation.isPending || updateWeekMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={isDayFormOpen || !!editingDay}
        onClose={() => {
          setIsDayFormOpen(false);
          setEditingDay(null);
        }}
        title={editingDay ? 'Editar Día' : 'Nuevo Día'}
      >
        <DayForm
          weekId={editingDay?.weekId || dayFormWeekId}
          initialData={editingDay || undefined}
          onSubmit={(data) => {
            if (editingDay) updateDayMutation.mutate({ dayId: editingDay.id, data });
            else createDayMutation.mutate(data);
          }}
          onCancel={() => {
            setIsDayFormOpen(false);
            setEditingDay(null);
          }}
          isLoading={createDayMutation.isPending || updateDayMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={isBlockFormOpen || !!editingBlock}
        onClose={() => {
          setIsBlockFormOpen(false);
          setEditingBlock(null);
          setBlockFormParentId(null);
        }}
        title={editingBlock ? 'Editar Bloque' : blockFormParentId ? 'Nuevo Sub-bloque' : 'Nuevo Bloque'}
      >
        <BlockForm
          dayId={editingBlock?.dayId || blockFormDayId}
          parentBlockId={editingBlock?.parentBlockId ?? blockFormParentId}
          discipline={plan.discipline}
          initialData={editingBlock || undefined}
          onSubmit={(data) => {
            if (editingBlock) updateBlockMutation.mutate({ blockId: editingBlock.id, data });
            else createBlockMutation.mutate(data);
          }}
          onCancel={() => {
            setIsBlockFormOpen(false);
            setEditingBlock(null);
            setBlockFormParentId(null);
          }}
          isLoading={createBlockMutation.isPending || updateBlockMutation.isPending}
        />
      </Modal>

      <Modal
        isOpen={isItemFormOpen || !!editingItem}
        onClose={() => {
          setIsItemFormOpen(false);
          setEditingItem(null);
        }}
        title={editingItem ? 'Editar Ejercicio' : 'Agregar Ejercicio'}
        size="lg"
      >
        <BlockItemForm
          blockId={editingItem?.blockId || itemFormBlockId}
          discipline={plan.discipline}
          initialData={editingItem || undefined}
          onSubmit={(data) => {
            if (editingItem)
              updateItemMutation.mutate({ blockId: editingItem.blockId, itemId: editingItem.id, data });
            else addItemMutation.mutate({ blockId: itemFormBlockId, data });
          }}
          onCancel={() => {
            setIsItemFormOpen(false);
            setEditingItem(null);
          }}
          isLoading={addItemMutation.isPending || updateItemMutation.isPending}
        />
      </Modal>
    </motion.div>
  );
}
